import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Truck, Plus, CheckCircle2, FileText, AlertCircle, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { CompanyContext, useCurrency, formatCurrency } from '../contexts/CompanyContext';
import RowActions from '../components/RowActions';
import { downloadEntityPdf } from '../lib/entityPdf';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-700',
  paid: 'bg-indigo-100 text-indigo-800',
};

export default function Procurement() {
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const { symbol } = useCurrency();

  const [tab, setTab] = useState('delivery');
  const [pos, setPos] = useState([]);
  const [dns, setDns] = useState([]);
  const [prs, setPrs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dnDraft, setDnDraft] = useState(null);
  const [prDraft, setPrDraft] = useState(null);
  const [chequeFor, setChequeFor] = useState(null);
  const [cheque, setCheque] = useState({ mode: 'cheque', cheque_number: '', bank_name: '' });

  const canApprove = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, d, r, s] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/delivery-notes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/payment-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setPos(p.data || []); setDns(d.data || []); setPrs(r.data || []); setSuppliers(s.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  // ---------- Delivery Notes ----------
  const newDn = (po) => {
    setDnDraft({
      po_id: po.id,
      po_number: po.po_number,
      supplier_id: po.supplier_id,
      delivery_date: new Date().toISOString().slice(0, 10),
      delivered_by: '',
      received_by: user?.name || user?.email || '',
      notes: '',
      items: (po.line_items || []).map(li => ({ item_name: li.item_name, quantity_ordered: li.quantity, quantity_received: li.quantity, unit: li.unit || 'pcs', condition: 'good' })),
    });
  };
  const saveDn = async () => {
    try {
      await axios.post(`${API}/delivery-notes`, { ...dnDraft, delivery_date: new Date(dnDraft.delivery_date).toISOString() }, { withCredentials: true });
      toast.success('Delivery note created'); setDnDraft(null); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const signDn = async (id) => { await axios.post(`${API}/delivery-notes/${id}/sign`, {}, { withCredentials: true }); toast.success('Signed by supervisor'); fetchAll(); };
  const removeDn = async (id) => { await axios.delete(`${API}/delivery-notes/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchAll(); };

  // ---------- Payment Requests ----------
  const newPr = (po) => {
    setPrDraft({ po_id: po.id, po_number: po.po_number, supplier_id: po.supplier_id, supplier_name: suppliers.find(s => s.id === po.supplier_id)?.name || 'Supplier', amount: po.total_amount || 0, currency: 'AED', purpose: `Payment for LPO ${po.po_number}`, urgency: 'normal' });
  };
  const savePr = async () => {
    try {
      await axios.post(`${API}/payment-requests`, prDraft, { withCredentials: true });
      toast.success('Payment request created'); setPrDraft(null); fetchAll();
    } catch (e) { toast.error('Save failed'); }
  };
  const submitPr = async (id) => { await axios.post(`${API}/payment-requests/${id}/submit`, {}, { withCredentials: true }); toast.success('Submitted'); fetchAll(); };
  const approvePr = async (id) => { await axios.post(`${API}/payment-requests/${id}/approve`, {}, { withCredentials: true }); toast.success('Approved'); fetchAll(); };
  const markPaid = async () => {
    try {
      await axios.post(`${API}/payment-requests/${chequeFor}/mark-paid`, cheque, { withCredentials: true });
      toast.success('Voucher created · payment recorded'); setChequeFor(null); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mark-paid failed'); }
  };

  // ---------- PDF helpers ----------
  const dnPdf = (dn) => downloadEntityPdf({
    title: `Delivery Note · ${dn.delivery_note_no}`,
    subtitle: `Against LPO ${dn.po_number}`,
    company,
    fields: [
      { label: 'Supplier', value: suppliers.find(s => s.id === dn.supplier_id)?.name || '—' },
      { label: 'Delivery date', value: dn.delivery_date?.split('T')[0] },
      { label: 'Delivered by', value: dn.delivered_by },
      { label: 'Received by', value: dn.received_by },
      { label: 'Status', value: dn.status },
      { label: 'Signed by supervisor', value: dn.signed_by_supervisor_name || '—' },
      { label: 'Signed at', value: dn.signed_at?.split('T')[0] || '—' },
      { label: 'Items', value: (dn.items || []).map(i => `${i.item_name} – ${i.quantity_received}/${i.quantity_ordered} ${i.unit || ''} (${i.condition})`).join('; ') },
    ],
    notes: dn.notes,
  });

  const prPdf = (pr) => downloadEntityPdf({
    title: `Payment Request · ${pr.request_no}`,
    subtitle: `Against LPO ${pr.po_number || '—'}`,
    company,
    fields: [
      { label: 'Supplier', value: pr.supplier_name },
      { label: 'Amount', value: formatCurrency(pr.amount, symbol) },
      { label: 'Urgency', value: pr.urgency },
      { label: 'Purpose', value: pr.purpose },
      { label: 'Status', value: pr.status },
      { label: 'Prepared by', value: pr.prepared_by_name },
      { label: 'Approved by', value: pr.approved_by_name },
      { label: 'Approved at', value: pr.approved_at?.split('T')[0] || '—' },
      { label: 'Voucher', value: pr.voucher_id || '—' },
    ],
  });

  return (
    <Layout>
      <div data-testid="procurement-page" className="space-y-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-600" />Procurement Chain
          </h1>
          <p className="text-sm text-muted-foreground">After an LPO is approved: Delivery Note → Service Report → Payment Request → Cheque issued. Each step stamped.</p>
        </div>

        <div className="flex gap-1 p-0.5 rounded-md bg-slate-100 w-fit">
          {[{ k: 'delivery', label: 'Delivery Notes' }, { k: 'payments', label: 'Payment Requests' }].map(o => (
            <button key={o.k} data-testid={`proc-tab-${o.k}`} onClick={() => setTab(o.k)} className={`px-4 py-1.5 rounded text-sm font-medium ${tab === o.k ? 'bg-white text-indigo-700 shadow' : 'text-slate-600'}`}>{o.label}</button>
          ))}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : tab === 'delivery' ? (
          <>
            <Card className="p-4">
              <h3 className="font-semibold text-foreground mb-2">Approved LPOs awaiting delivery</h3>
              <div className="space-y-2">
                {pos.filter(p => ['approved', 'received', 'pending_approval'].includes(p.status)).map(p => (
                  <div key={p.id} data-testid={`po-row-${p.id}`} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                    <span className="font-mono text-xs text-muted-foreground">{p.po_number}</span>
                    <span className="font-medium">{suppliers.find(s => s.id === p.supplier_id)?.name || '—'}</span>
                    <span className="text-xs text-muted-foreground">{formatCurrency(p.total_amount || 0, symbol)}</span>
                    <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[p.status] || 'bg-slate-100'}`}>{p.status}</span>
                    <Button size="sm" variant="outline" onClick={() => newDn(p)} data-testid={`po-new-dn-${p.id}`}><Plus className="w-3.5 h-3.5 mr-1" />Delivery Note</Button>
                  </div>
                ))}
                {pos.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">No LPOs yet</div>}
              </div>
            </Card>

            <div className="grid gap-3">
              {dns.map(dn => (
                <Card key={dn.id} data-testid={`dn-card-${dn.id}`} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-muted-foreground">{dn.delivery_note_no}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[dn.status] || 'bg-slate-100'}`}>{dn.status}</span>
                        <span className="text-xs text-muted-foreground">→ LPO {dn.po_number}</span>
                        {dn.signed_by_supervisor_name && <span className="text-[10px] text-emerald-700">✓ {dn.signed_by_supervisor_name}</span>}
                      </div>
                      <div className="text-sm text-slate-700">{dn.items?.length || 0} items · delivered {dn.delivery_date?.split('T')[0]}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {dn.status === 'draft' && (
                        <Button size="sm" variant="outline" onClick={() => signDn(dn.id)} data-testid={`dn-sign-${dn.id}`} className="text-emerald-600 border-emerald-600"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Sign</Button>
                      )}
                      <RowActions
                        testIdPrefix={`dn-${dn.id}`}
                        onDownloadPdf={() => dnPdf(dn)}
                        onDelete={() => removeDn(dn.id)}
                        deleteLabel={`delivery note ${dn.delivery_note_no}`}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            <Card className="p-4">
              <h3 className="font-semibold text-foreground mb-2">LPOs ready for payment</h3>
              <div className="space-y-2">
                {pos.filter(p => ['received', 'paid', 'approved'].includes(p.status)).map(p => (
                  <div key={p.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 last:border-0">
                    <span className="font-mono text-xs text-muted-foreground">{p.po_number}</span>
                    <span className="font-medium">{suppliers.find(s => s.id === p.supplier_id)?.name || '—'}</span>
                    <span className="text-xs text-muted-foreground">{formatCurrency(p.total_amount || 0, symbol)}</span>
                    <Button size="sm" variant="outline" className="ml-auto" onClick={() => newPr(p)} data-testid={`po-new-pr-${p.id}`}><Plus className="w-3.5 h-3.5 mr-1" />Payment Request</Button>
                  </div>
                ))}
              </div>
            </Card>

            <div className="grid gap-3">
              {prs.map(pr => (
                <Card key={pr.id} data-testid={`pr-card-${pr.id}`} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-muted-foreground">{pr.request_no}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_COLORS[pr.status] || 'bg-slate-100'}`}>{pr.status}</span>
                        <span className="text-xs text-muted-foreground">→ LPO {pr.po_number}</span>
                        {pr.urgency === 'urgent' && <span className="text-[10px] text-rose-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Urgent</span>}
                      </div>
                      <div className="font-semibold text-foreground">{pr.supplier_name}</div>
                      <div className="text-xs text-muted-foreground">{pr.purpose}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Amount</div>
                        <div className="text-lg font-bold text-indigo-700">{formatCurrency(pr.amount, symbol)}</div>
                      </div>
                      {pr.status === 'draft' && <Button size="sm" variant="outline" onClick={() => submitPr(pr.id)} data-testid={`pr-submit-${pr.id}`}>Submit</Button>}
                      {pr.status === 'submitted' && canApprove && <Button size="sm" variant="outline" onClick={() => approvePr(pr.id)} className="text-emerald-600 border-emerald-600" data-testid={`pr-approve-${pr.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>}
                      {pr.status === 'approved' && canApprove && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setChequeFor(pr.id); setCheque({ mode: 'cheque', cheque_number: '', bank_name: '' }); }} data-testid={`pr-paid-${pr.id}`}><Wallet className="w-3.5 h-3.5 mr-1" />Issue Cheque</Button>}
                      <RowActions testIdPrefix={`pr-${pr.id}`} onDownloadPdf={() => prPdf(pr)} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Delivery Note dialog */}
        <Dialog open={!!dnDraft} onOpenChange={(v) => { if (!v) setDnDraft(null); }}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Delivery Note</DialogTitle></DialogHeader>
            {dnDraft && (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-3 gap-2 text-sm">
                  <div><Label>LPO</Label><Input value={dnDraft.po_number} disabled /></div>
                  <div><Label>Delivery date</Label><Input type="date" value={dnDraft.delivery_date} onChange={(e) => setDnDraft({ ...dnDraft, delivery_date: e.target.value })} /></div>
                  <div><Label>Delivered by</Label><Input value={dnDraft.delivered_by} onChange={(e) => setDnDraft({ ...dnDraft, delivered_by: e.target.value })} placeholder="Supplier rep" /></div>
                </div>
                <Label>Items received</Label>
                <div className="overflow-x-auto -mx-1">
                <table className="w-full text-xs border border-slate-200 rounded min-w-[520px]">
                  <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Item</th><th>Ordered</th><th>Received</th><th>Unit</th><th>Condition</th></tr></thead>
                  <tbody>
                    {dnDraft.items.map((it, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1"><Input value={it.item_name} onChange={(e) => { const items = [...dnDraft.items]; items[i] = { ...it, item_name: e.target.value }; setDnDraft({ ...dnDraft, items }); }} /></td>
                        <td className="text-center"><Input type="number" value={it.quantity_ordered} onChange={(e) => { const items = [...dnDraft.items]; items[i] = { ...it, quantity_ordered: parseFloat(e.target.value) }; setDnDraft({ ...dnDraft, items }); }} /></td>
                        <td className="text-center"><Input data-testid={`dn-qty-recv-${i}`} type="number" value={it.quantity_received} onChange={(e) => { const items = [...dnDraft.items]; items[i] = { ...it, quantity_received: parseFloat(e.target.value) }; setDnDraft({ ...dnDraft, items }); }} /></td>
                        <td className="text-center"><Input value={it.unit} onChange={(e) => { const items = [...dnDraft.items]; items[i] = { ...it, unit: e.target.value }; setDnDraft({ ...dnDraft, items }); }} /></td>
                        <td><Select value={it.condition} onValueChange={(v) => { const items = [...dnDraft.items]; items[i] = { ...it, condition: v }; setDnDraft({ ...dnDraft, items }); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="good">Good</SelectItem><SelectItem value="damaged">Damaged</SelectItem><SelectItem value="partial">Partial</SelectItem></SelectContent></Select></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <Textarea rows={2} placeholder="Notes" value={dnDraft.notes} onChange={(e) => setDnDraft({ ...dnDraft, notes: e.target.value })} />
                <Button onClick={saveDn} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="dn-save-btn">Create Delivery Note</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Payment Request dialog */}
        <Dialog open={!!prDraft} onOpenChange={(v) => { if (!v) setPrDraft(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New Payment Request</DialogTitle></DialogHeader>
            {prDraft && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>LPO</Label><Input value={prDraft.po_number} disabled /></div>
                <div><Label>Supplier</Label><Input value={prDraft.supplier_name} onChange={(e) => setPrDraft({ ...prDraft, supplier_name: e.target.value })} /></div>
                <div><Label>Amount</Label><Input data-testid="pr-amount-input" type="number" value={prDraft.amount} onChange={(e) => setPrDraft({ ...prDraft, amount: parseFloat(e.target.value) })} /></div>
                <div><Label>Urgency</Label><Select value={prDraft.urgency} onValueChange={(v) => setPrDraft({ ...prDraft, urgency: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent></Select></div>
                <div className="sm:col-span-2"><Label>Purpose</Label><Textarea rows={2} value={prDraft.purpose} onChange={(e) => setPrDraft({ ...prDraft, purpose: e.target.value })} /></div>
                <div className="sm:col-span-2"><Button onClick={savePr} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="pr-save-btn">Create Payment Request</Button></div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Issue Cheque dialog */}
        <Dialog open={!!chequeFor} onOpenChange={(v) => { if (!v) setChequeFor(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>Issue Cheque</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Mode</Label><Select value={cheque.mode} onValueChange={(v) => setCheque({ ...cheque, mode: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent></Select></div>
              {cheque.mode === 'cheque' && <>
                <div><Label>Cheque #</Label><Input data-testid="cheque-no-input" value={cheque.cheque_number} onChange={(e) => setCheque({ ...cheque, cheque_number: e.target.value })} /></div>
                <div><Label>Bank</Label><Input value={cheque.bank_name} onChange={(e) => setCheque({ ...cheque, bank_name: e.target.value })} /></div>
              </>}
              <Button onClick={markPaid} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="cheque-confirm-btn">Confirm payment & generate Voucher</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
